import { useCallback, useEffect, useState } from "react";
import { usePatientStats } from "../fhir/patient/hooks/usePatientStats";
import { useProtocolStats } from "../fhir/protocol/hooks/useProtocolStats";
import { usePractitionerRoleContext } from "../../contexts/PractitionerRoleContext";

export interface QuickStatsData {
  patients: {
    total: number;
    loading: boolean;
  };
  doctors: {
    total: number;
    loading: boolean;
  };
  protocols: {
    total: number;
    loading: boolean;
  };
}

/**
 * Hook centralizado para gerenciar todas as estatísticas rápidas
 * Combina dados de pacientes, médicos e protocolos em um único lugar
 *
 * @example
 * ```tsx
 * const { stats, loading, error, refetch } = useQuickStats();
 *
 * // Usar os dados
 * console.log(stats.patients.total);
 * ```
 */
export function useQuickStats() {
  const [error, setError] = useState<Error | null>(null);

  // Hook para pacientes
  const {
    data: patientsData,
    loading: loadingPatients,
    error: patientsError,
    fetchStats: fetchPatients,
  } = usePatientStats();

  // Hook para médicos
  const { practitionerRole, practitionerRoleLoading } =
    usePractitionerRoleContext();

  // Hook para protocolos
  const {
    data: protocolsData,
    loading: loadingProtocols,
    error: protocolsError,
    fetchStats: fetchProtocols,
  } = useProtocolStats();

  // Função para buscar todos os dados
  const refetch = useCallback(async () => {
    try {
      await Promise.all([fetchPatients(), fetchProtocols()]);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Erro ao buscar estatísticas")
      );
    }
  }, [fetchPatients, fetchProtocols]);

  // Efeito para buscar dados iniciais
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Combina todos os erros
  useEffect(() => {
    if (patientsError) setError(patientsError);
    if (protocolsError) setError(protocolsError);
  }, [patientsError, protocolsError]);

  const stats: QuickStatsData = {
    patients: {
      total: patientsData?.total || 0,
      loading: loadingPatients,
    },
    doctors: {
      total: practitionerRole?.length || 0,
      loading: practitionerRoleLoading,
    },
    protocols: {
      total: protocolsData?.entry?.length || 0,
      loading: loadingProtocols,
    },
  };

  const loading =
    loadingPatients || practitionerRoleLoading || loadingProtocols;

  return {
    stats,
    loading,
    error,
    refetch,
  };
}
